import type { WritableAtom } from "jotai";
import { useAtom } from "jotai";
import { useEffect } from "react";

/**
 * Generic hook for consuming commands from Jotai atoms.
 *
 * When a command is received:
 * 1. Executes the provided callback with the command
 * 2. Automatically resets the atom to null
 *
 * This enables one-time command execution patterns where multiple components
 * can send commands through the atom, and the consumer processes each command once.
 *
 * @template T The command type
 * @param commandAtom The Jotai atom containing the command (must be WritableAtom<T | null>)
 * @param onCommand Callback executed when a command is received. Should be wrapped in useCallback.
 *
 * @example
 * ```tsx
 * const handleCommand = useCallback((command: SidebarCommand) => {
 *   if (command.action === 'PREVIEW_DOCUMENT') {
 *     openDocument(command.payload.documentId, command.payload.page);
 *   }
 * }, [openDocument]);
 *
 * useCommandEffect(sidebarCommandAtom, handleCommand);
 * ```
 */
export const useCommandEffect = <T>(
  commandAtom: WritableAtom<
    T | null,
    [T | null | ((prev: T | null) => T | null)],
    void
  >,
  onCommand: (command: T) => void | Promise<void>,
) => {
  const [command, setCommand] = useAtom(commandAtom);

  useEffect(() => {
    if (command) {
      void onCommand(command);
      setCommand(null);
    }
  }, [command, setCommand, onCommand]);
};
